import React, { useMemo, useState } from 'react';
import { Button, Dropdown, Input } from 'antd';
import { useLayout } from '@layouts/PageLayout';
import { SearchIcon, SortArrowIcon, SortListIcon, SortTilesIcon } from '@icons';
import { AssetControllerListAssetsParams } from '@api/Api';

type AssetsOrderBy = NonNullable<
  AssetControllerListAssetsParams['orderBy']
>[number];

const assetsOrderByKey = 'assets-order-by';
export const defaultAssetsOrder = 'order:DESC';

const assetsOrderOptions: AssetsOrderBy[] = [
  'order:DESC',
  'createdAt:DESC',
  'modifiedAt:DESC',
  'name:ASC'
];

export function getInitialAssetsOrderBy() {
  return localStorage.getItem(assetsOrderByKey) || defaultAssetsOrder;
}

/** Saved assets order, limited to the options offered in the sort menus */
export function getInitialAssetsOrderByParam(): AssetsOrderBy {
  const orderBy = getInitialAssetsOrderBy() as AssetsOrderBy;
  return assetsOrderOptions.includes(orderBy) ? orderBy : defaultAssetsOrder;
}

export function saveAssetsOrderBy(orderBy: string) {
  localStorage.setItem(assetsOrderByKey, orderBy);
}

export default function AssetsTabControls({
  searchQuery,
  orderBy,
  onSearchChange,
  onOrderByChange
}: {
  searchQuery: string;
  orderBy: string;
  onSearchChange: (value: string) => void;
  onOrderByChange: (value: string) => void;
}) {
  const { currentView, setCurrentView } = useLayout();
  const isTileView = currentView === 'tile';
  const [selectedSorter, setSelectedSorter] = useState(orderBy);

  const items = useMemo(
    () => [
      {
        key: 'order:DESC',
        label: (
          <>
            <span className="help_dropdown__dot" />
            Custom
          </>
        )
      },
      {
        key: 'createdAt:DESC',
        label: (
          <>
            <span className="help_dropdown__dot" />
            Date created
          </>
        )
      },
      {
        key: 'modifiedAt:DESC',
        label: (
          <>
            <span className="help_dropdown__dot" />
            Date modified
          </>
        )
      },
      {
        key: 'name:ASC',
        label: (
          <>
            <span className="help_dropdown__dot" />
            Name
          </>
        )
      }
    ],
    []
  );

  return (
    <div className="campaign-tab-controls" id="assets-tab-operations">
      <Input
        style={{
          width: '100%',
          maxWidth: 230,
          padding: '8px 12px'
        }}
        placeholder="Search media or folder"
        prefix={<SearchIcon />}
        size="large"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      <div className="campaign-tab-controls__actions">
        <Dropdown
          getPopupContainer={() =>
            document.querySelector('#assets-tab-operations') as HTMLElement
          }
          menu={{
            items: [
              {
                type: 'group',
                label: 'Sort by'
              },
              ...items
            ],
            selectedKeys: [selectedSorter],
            onClick: ({ key }) => {
              setSelectedSorter(key);
              onOrderByChange(key);
              saveAssetsOrderBy(key);
            }
          }}
          placement="bottomLeft"
          overlayClassName="help_dropdown"
          trigger={['click']}
        >
          <Button
            style={{ marginRight: 20, height: 'auto' }}
            type="text"
            icon={<SortArrowIcon />}
          >
            Sort
          </Button>
        </Dropdown>
        <Button
          type="text"
          style={{ padding: 0, height: 'auto' }}
          className="select-assets-view-btn"
          onClick={() => {
            setCurrentView('tile');
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }}
        >
          <SortTilesIcon
            style={{ marginRight: 0 }}
            className={isTileView ? 'select-assets-view-icon' : undefined}
          />
        </Button>
        <div className="line" />
        <Button
          type="text"
          className="select-assets-view-btn"
          style={{ padding: 0, height: 'auto' }}
          onClick={() => {
            setCurrentView('list');
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }}
        >
          <SortListIcon
            style={{ marginRight: 0 }}
            className={!isTileView ? 'select-assets-view-icon' : undefined}
          />
        </Button>
      </div>
    </div>
  );
}
